
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { graft, Delete, ReplacePatch, IPatch } from '../../utilities/Graft';
import { Fragment } from '../../utilities/components/Fragment';
import { IFacet } from '../../models/Facet';
import { IComponentDecorator, IDecoratedProps } from '../../utilities/components/Decorators';

/**
 * @internal
 */
export interface ILayoutState {
  facet: IFacet<{}>;
  isAvailable: boolean;
  content: JSX.Element;
}

/**
 * @internal
 */
export interface IUpdateLayoutOptions {
  key: string;
  state: IPatch<ILayoutState>;
}

/**
 * @internal
 */
export interface IRemoveLayoutOptions {
  key: string;
}

/**
 * @internal
 */
export interface IUpdateChildOptions {
  layouts: {
    [key: string]: ILayoutState;
  };
}

/**
 * @internal
 */
export interface ILayoutManagerContext {
  layoutManager?: {
    updateLayout: (options: IUpdateLayoutOptions) => void;
    removeLayout: (options: IRemoveLayoutOptions) => void;
    updateChild: (options: IUpdateChildOptions) => void;
  };
}

/**
 * @internal
 */
export const LAYOUT_CONTEXT_TYPES: React.ValidationMap<ILayoutManagerContext> = {
  layoutManager: PropTypes.object
};

/**
 * @internal
 */
export interface ILayoutManagerProps {
  onUpdateLayouts?: (layouts: {
    [key: string]: ILayoutState;
  }) => void;
}

/**
 * @internal
 */
export interface ILayoutManagerState {
  layouts: {
    [key: string]: ILayoutState;
  };
  child: {
    layouts: {
      [key: string]: ILayoutState;
    };
  };
}

/**
 * @internal
 */
export class LayoutManager extends React.PureComponent<ILayoutManagerProps, ILayoutManagerState> {
  public static readonly childContextTypes: typeof LAYOUT_CONTEXT_TYPES = LAYOUT_CONTEXT_TYPES;
  public static readonly contextTypes: typeof LAYOUT_CONTEXT_TYPES = LAYOUT_CONTEXT_TYPES;

  public context!: ILayoutManagerContext;

  constructor(props: ILayoutManagerProps, context: ILayoutManagerContext) {
    super(props, context);

    this.state = {
      layouts: {},
      child: {
        layouts: {}
      }
    };
  }

  public getChildContext(): ILayoutManagerContext {
    return {
      layoutManager: {
        updateLayout: this._onUpdateLayout,
        removeLayout: this._onRemoveLayout,
        updateChild: this._onUpdateChild
      }
    };
  }

  public render(): JSX.Element {
    return (
      <Fragment hidden={ true }>
        { this.props.children }
      </Fragment>
    );
  }

  public componentDidMount(): void {
    this._updateLayouts(this.state, this.props);
  }

  public componentDidUpdate(previousProps: ILayoutManagerProps, previousState: ILayoutManagerState): void {
    if (previousState.layouts !== this.state.layouts || previousState.child !== this.state.child) {
      this._updateLayouts(this.state, this.props);
    }
  }

  private _updateLayouts(state: ILayoutManagerState, props: ILayoutManagerProps): void {
    const layouts: {
      [key: string]: ILayoutState;
    } = {};

    for (const key in state.child.layouts) {
      if (Object.prototype.hasOwnProperty.call(state.child.layouts, key)) {
        layouts[key] = state.child.layouts[key];
      }
    }

    for (const key in state.layouts) {
      if (Object.prototype.hasOwnProperty.call(state.layouts, key)) {
        layouts[key] = state.layouts[key];
      }
    }

    const {
      layoutManager
    } = this.context;

    if (layoutManager) {
      // Cascade the layout updates up the tree.
      layoutManager.updateChild({
        layouts: layouts
      });
    }

    const {
      onUpdateLayouts
    } = props;

    if (onUpdateLayouts) {
      onUpdateLayouts(layouts);
    }
  }

  @autobind
  private _onUpdateLayout(options: IUpdateLayoutOptions): void {
    this.setState((previousState: ILayoutManagerState): ILayoutManagerState => {
      return graft(previousState, {
        layouts: {
          [options.key]: options.state
        }
      });
    });
  }

  @autobind
  private _onRemoveLayout(options: IRemoveLayoutOptions): void {
    this.setState((previousState: ILayoutManagerState): ILayoutManagerState => {
      return graft(previousState, {
        layouts: {
          [options.key]: new Delete<ILayoutState>()
        }
      });
    });
  }

  @autobind
  private _onUpdateChild(options: IUpdateChildOptions): void {
    this.setState((previousState: ILayoutManagerState): ILayoutManagerState => {
      return graft(previousState, {
        child: {
          layouts: new ReplacePatch(options.layouts)
        }
      });
    });
  }
}

/**
 * @public
 */
export interface ILayoutProps {
  isAvailable?: boolean;
}

/**
 * @public
 */
export type IDecorateAsLayoutProps<TProps> = IDecoratedProps<TProps, {}, ILayoutProps>;

/**
 * Wraps a component and makes it available as a registered layout.
 * @public
 */
export function asLayout(
  layoutType: IFacet<{}>
): IComponentDecorator<{}, ILayoutProps> {
  return <P extends {}>(
    component: React.ComponentType<P>
  ): React.ComponentType<IDecoratedProps<P, {}, ILayoutProps>> => {
    const Component = component;
    const key = layoutType.id;

    type IProps = IDecoratedProps<P, {}, ILayoutProps>;

    class Layout extends React.PureComponent<IProps, {}> {
      public static readonly contextTypes: typeof LAYOUT_CONTEXT_TYPES = LAYOUT_CONTEXT_TYPES;

      public context!: ILayoutManagerContext;

      constructor(props: IProps, context?: ILayoutManagerContext) {
        super(props, context);
      }

      public render(): JSX.Element | null {
        return null;
      }

      public componentDidMount(): void {
        const {
          isAvailable = true,
          ...componentProps
        } = this.props as ILayoutProps;

        const content = <Component { ...componentProps } />;

        const {
          layoutManager
        } = this.context;

        if (layoutManager) {
          layoutManager.updateLayout({
            key: key,
            state: {
              facet: layoutType,
              isAvailable: isAvailable,
              content: new ReplacePatch(content)
            }
          });
        }
      }

      public componentDidUpdate(): void {
        const {
          isAvailable = true,
          ...componentProps
        } = this.props as ILayoutProps;

        const content = <Component { ...componentProps } />;

        const {
          layoutManager
        } = this.context;

        if (layoutManager) {
          layoutManager.updateLayout({
            key: key,
            state: {
              facet: layoutType,
              isAvailable: isAvailable,
              content: new ReplacePatch(content)
            }
          });
        }
      }

      public componentWillUnmount(): void {
        const {
          layoutManager
        } = this.context;

        if (layoutManager) {
          layoutManager.removeLayout({
            key: key
          });
        }
      }
    }

    return Layout;
  };
}
